import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';

import { ApiUrlInterceptor } from './core/api/api-url-interceptor';
import { ApIErrorInterceptor } from './core/api/api-error-interceptors';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AppErrorHandler } from './core/app-error-handler';
import { RouterModule, Routes } from '@angular/router';
import { AppInitComponent } from './app-init/app-init.component';
import { NewCoreModule } from './core/new-core.module';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { IAppConfig } from './core/config/app-config.model';
import { environment } from '../environments/environment';
import { LoaderInterceptor } from './rp/core/loader/loader.interceptor';

import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { UiService } from './core/ui.service';
// Import the module from the SDK
import { AuthModule } from '@auth0/auth0-angular';

const cEnvironment = environment as any;

const routers: Routes = [
  {path: '', loadChildren: () => import('./app-component.module').then(m => m.AppComponentModule)},
];

// read from config
function initializeKeycloak(keycloak: KeycloakService,  config: IAppConfig) {
  if(!cEnvironment.keyCloakConfig) return () => {};

  return () => {
    return new Promise((resolve, reject) => {
      keycloak.init({
        config: {
          url: cEnvironment.keyCloakConfig?.url || '',
          realm: cEnvironment.keyCloakConfig?.realm || '',
          clientId: cEnvironment.keyCloakConfig?.clientId || ''
        },
        initOptions: {
          checkLoginIframe: false
        },
        enableBearerInterceptor: false
      }).then(() => {
        resolve(true);
      })
      .catch((err) => {
        console.error('Failed to init keycloak', err);
        resolve(false);
      });
    })
  }
}

function getOktaRedirectURL(): string{
  let tag = document.querySelector('base');
  if(tag){
    let url = tag.href;
    if(url !== '' && url !== '/'){
      return url;
    }
  }
  return window.location.origin;
}

@NgModule({
  declarations: [
    AppInitComponent
  ],
  imports: [
    KeycloakAngularModule,
    BrowserModule,
    BrowserAnimationsModule,

    RouterModule.forRoot(routers),

    NewCoreModule,

    NgIdleKeepaliveModule.forRoot(),

    AuthModule.forRoot({
      domain: cEnvironment.oktaConfig ? cEnvironment.oktaConfig.domain : '',
      clientId: cEnvironment.oktaConfig ? cEnvironment.oktaConfig.clientId: '',
      authorizationParams: {
        redirect_uri: getOktaRedirectURL()
      },
      skipRedirectCallback: window.location.search.indexOf('error=') !== -1 || window.location.href.indexOf('auth0/callback') !== -1,
    }),

    // google tag manager
    ...(cEnvironment.googleTagManagerId
      ? [GoogleTagManagerModule.forRoot({ id: cEnvironment.googleTagManagerId })]
      : []),
  ],
  providers: [
    ...(cEnvironment.isUsingExternalIdentityServer && cEnvironment.externalAuthorizationProvider === 'Keycloak' ? [{
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService]
    }] : []),
    MessageService,
    ConfirmationService,
    provideHttpClient(
      withInterceptorsFromDi()
    ),
    { provide: HTTP_INTERCEPTORS, useClass: ApiUrlInterceptor, multi: true},
    { provide: HTTP_INTERCEPTORS, useClass: ApIErrorInterceptor, multi: true},
    
    // override error handle from 
    { provide: ErrorHandler, useClass: AppErrorHandler },

    // Loader service
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    UiService,
  ],
  bootstrap: [AppInitComponent],
  exports:[
  ]
})
export class AppModule {

  constructor() {    
  }
 }
